import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
const testimonials = [{
  name: 'Carlos M.',
  location: 'Katy, TX',
  initials: 'CM',
  rating: 5,
  text: "I spent months looking for someone reliable. RMorales showed up on time, did an incredible job, and the price was fair. They're my permanent contractors now."
}, {
  name: 'Jennifer R.',
  location: 'Cypress, TX',
  initials: 'JR',
  rating: 5,
  text: 'What impressed me most was the cleanliness. Other services mow the grass but leave everything dirty. RMorales left my lawn looking brand new.'
}, {
  name: 'Roberto G.',
  location: 'Richmond, TX',
  initials: 'RG',
  rating: 5,
  text: 'My HOA had sent me two notices. I called RMorales on Monday and by Wednesday my lawn was perfect. I never worry about it anymore.'
}, {
  name: 'Maria S.',
  location: 'Fulshear, TX',
  initials: 'MS',
  rating: 5,
  text: 'Professional, punctual, and the results speak for themselves. They transformed my backyard into something I am proud to show off to my friends.'
}, {
  name: 'David T.',
  location: 'Rosenberg, TX',
  initials: 'DT',
  rating: 5,
  text: 'Best lawn service I have ever hired. Fair pricing, excellent communication, and they always go the extra mile. Highly recommended!'
}];
const AVATAR_COLORS = ['bg-[#2D5A27]', 'bg-[#C5A55A]', 'bg-[#1B2E1B]', 'bg-[#2D5A27]', 'bg-[#C5A55A]'];
export function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setDirection(1);
      setActiveIndex(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused]);
  const handleNext = () => {
    setDirection(1);
    setActiveIndex(prev => (prev + 1) % testimonials.length);
  };
  const handlePrev = () => {
    setDirection(-1);
    setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const handleDotClick = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };
  const current = testimonials[activeIndex];
  return <section id="testimonials" className="py-16 md:py-24 bg-white overflow-hidden" data-id="element-313">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-314">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-315">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C5A55A]/10 text-[#C5A55A] mb-6" data-id="element-316">
            <Star className="w-8 h-8 fill-current" data-id="element-317" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1B2E1B] mb-4" data-id="element-318">
            WHAT THE NEIGHBORS ARE SAYING
          </h2>
          <div className="flex items-center justify-center gap-2 mb-2" data-id="element-319">
            {[...Array(5)].map((_, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1,
            type: 'spring',
            stiffness: 200
          }} data-id="element-320">
                <Star className="w-6 h-6 fill-[#C5A55A] text-[#C5A55A]" data-id="element-321" />
              </motion.div>)}
            <span className="ml-2 text-lg font-bold text-[#1B2E1B]" data-id="element-322">5.0</span>
          </div>
          <p className="text-gray-500 text-sm" data-id="element-323">
            Based on real customer experiences
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="relative" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-324">
          <div className="relative min-h-[340px] md:min-h-[300px]" data-id="element-325">
            <AnimatePresence mode="wait" custom={direction} data-id="element-326">
              <motion.div key={activeIndex} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 60 : -60
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -60 : 60
            }} transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="bg-gradient-to-br from-[#f9f7f2] to-[#f0ede4] rounded-3xl p-8 md:p-12 relative shadow-sm border border-gray-100" data-id="element-327">
                <Quote className="absolute top-8 right-8 w-16 h-16 text-[#C5A55A]/15" data-id="element-328" />

                {/* Stars */}
                <div className="flex gap-1 mb-6" data-id="element-329">
                  {[...Array(current.rating)].map((_, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  scale: 0,
                  rotate: -90
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  rotate: 0
                }} transition={{
                  delay: i * 0.08,
                  type: 'spring'
                }} data-id="element-330">
                      <Star className="w-6 h-6 fill-[#C5A55A] text-[#C5A55A]" data-id="element-331" />
                    </motion.div>)}
                </div>

                {/* Testimonial text */}
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8 italic relative z-10" data-id="element-332">
                  "{current.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4 border-t border-gray-200 pt-6" data-id="element-333">
                  <div className={`w-14 h-14 rounded-full ${AVATAR_COLORS[activeIndex % AVATAR_COLORS.length]} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`} data-id="element-334">
                    {current.initials}
                  </div>
                  <div data-id="element-335">
                    <p className="font-bold text-[#1B2E1B] text-lg" data-id="element-336">
                      {current.name}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-337">{current.location}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation arrows */}
          <button onClick={handlePrev} className="absolute left-0 md:-left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center text-[#1B2E1B] hover:bg-[#1B2E1B] hover:text-white transition-colors z-10" aria-label="Previous testimonial" data-id="element-338">
            <ChevronLeft className="w-5 h-5" data-id="element-339" />
          </button>
          <button onClick={handleNext} className="absolute right-0 md:-right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center text-[#1B2E1B] hover:bg-[#1B2E1B] hover:text-white transition-colors z-10" aria-label="Next testimonial" data-id="element-340">
            <ChevronRight className="w-5 h-5" data-id="element-341" />
          </button>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-8" data-id="element-342">
            {testimonials.map((_, i) => <button key={i} onClick={() => handleDotClick(i)} className={`h-2 rounded-full transition-all ${i === activeIndex ? 'w-8 bg-[#C5A55A]' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to testimonial ${i + 1}`} data-id="element-343" />)}
          </div>
        </div>

        {/* Google Reviews CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mt-12 text-center" data-id="element-344">
          <a href="https://share.google/nsS1VgtwzRcQrill4" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-[#1B2E1B] text-[#1B2E1B] font-bold hover:bg-[#1B2E1B] hover:text-white transition-all duration-300 shadow-sm hover:shadow-lg" data-id="element-345">
            <Star className="w-5 h-5 fill-current" data-id="element-346" />
            See More Reviews on Google
            <ExternalLink className="w-4 h-4" data-id="element-347" />
          </a>
        </motion.div>
      </div>
    </section>;
}